import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaPaperPlane, FaTimes } from 'react-icons/fa';
import { useFinance } from '../context/FinanceContext';

const SUGGESTED_QUESTIONS = [
    'How much on food?',
    'Am I on track?',
    'Biggest expense?',
    'Daily average?',
    'How much saved?',
    'Compare to last month',
];

const AIChatbox = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', text: 'Strategic AI active. Ask me about spending, savings, or your finances.' }
    ]);
    const [input, setInput] = useState('');
    const { transactions, getStats, targets, settings } = useFinance();
    const scrollRef = useRef();

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const processQuery = (query) => {
        const q = query.toLowerCase().trim();
        const stats = getStats();
        const txns = transactions || [];

        // Category spend queries
        const catMatch = q.match(/(?:how much|spending|spent)\s+(?:on|for)\s+(\w+)/i);
        if (catMatch) {
            const cat = catMatch[1].charAt(0).toUpperCase() + catMatch[1].slice(1);
            const amount = stats.categoryBreakdown?.[cat] || 0;
            if (amount > 0) return `You've spent ₹${amount.toLocaleString()} on ${cat} this month.`;
            return `No spending found for "${cat}" this month. Available categories: ${Object.keys(stats.categoryBreakdown || {}).join(', ') || 'none yet'}`;
        }

        // "Am I on track?"
        if (/on track|target|goal/i.test(q)) {
            if (targets.monthlySavingsGoal > 0) {
                const pct = ((stats.profit / targets.monthlySavingsGoal) * 100).toFixed(1);
                return `You're at ${pct}% of your ₹${targets.monthlySavingsGoal.toLocaleString()} savings target. ${Number(pct) >= 100 ? 'Target achieved!' : Number(pct) >= 70 ? 'Good pace — keep it up!' : 'You need to cut expenses or boost income.'}`;
            }
            return 'No savings target set. Configure one in Settings to track progress.';
        }

        // "Biggest expense?"
        if (/biggest|largest|top|highest/i.test(q) && /expense|spend|transaction/i.test(q)) {
            const debits = txns.filter(t => t.type === 'debit' || t.type === 'expense').sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
            if (debits.length > 0) {
                const top = debits[0];
                return `Biggest expense: ₹${Math.abs(top.amount).toLocaleString()} — "${top.description || top.category || 'Unknown'}" on ${(top.date || '').slice(0, 10)}.`;
            }
            return 'No expenses found this month.';
        }

        // "Compare to last month"
        if (/compare|last month|previous/i.test(q)) {
            const perf = settings?.lastMonthPerformance;
            if (perf && perf.income > 0) {
                return `Last month: Income ₹${perf.income.toLocaleString()}, Spend ₹${perf.spend.toLocaleString()}, Savings ₹${perf.savings.toLocaleString()}. This month so far: Income ₹${stats.income.toLocaleString()}, Expenses ₹${stats.expenses.toLocaleString()}, Savings ₹${stats.profit.toLocaleString()}.`;
            }
            return 'No last month data available yet. Complete a full month to compare.';
        }

        // "Daily average"
        if (/daily\s*(average|avg|spend)/i.test(q)) {
            const today = new Date();
            const daysPassed = today.getDate();
            const avgSpend = daysPassed > 0 ? (stats.expenses / daysPassed) : 0;
            return `Daily average spend: ₹${avgSpend.toFixed(0)} (₹${stats.expenses.toLocaleString()} over ${daysPassed} days). Your daily budget cap is ₹${stats.dailySpendCap?.toFixed(0) || '—'}.`;
        }

        // "How much saved?"
        if (/saved|savings|profit/i.test(q)) {
            return `This month: Income ₹${stats.income.toLocaleString()}, Expenses ₹${stats.expenses.toLocaleString()}, Net savings: ₹${stats.profit.toLocaleString()}. Savings rate: ${stats.savingsRate?.toFixed(1) || 0}%.`;
        }

        // "Income" queries
        if (/income|inflow|earned/i.test(q)) {
            return `Monthly income so far: ₹${stats.income.toLocaleString()}. Target: ₹${(settings?.monthlyIncomeTarget || 0).toLocaleString()}.`;
        }

        // "Expenses" / "total spend"
        if (/expense|total spend|outflow/i.test(q)) {
            return `Total expenses this month: ₹${stats.expenses.toLocaleString()} across ${stats.transactionCount} transactions.`;
        }

        // Last week spend
        if (/last\s*week|past\s*7/i.test(q)) {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            const spend = txns
                .filter(t => new Date(t.date) >= weekAgo && (t.type === 'expense' || t.type === 'debit'))
                .reduce((acc, curr) => acc + Math.abs(curr.amount || 0), 0);
            return `You spent ₹${spend.toLocaleString()} in the last 7 days. Daily budget: ₹${stats.dailySpendCap?.toFixed(0) || '—'}.`;
        }

        // Category breakdown
        if (/categor|breakdown/i.test(q)) {
            const cats = Object.entries(stats.categoryBreakdown || {}).sort((a, b) => b[1] - a[1]);
            if (cats.length === 0) return 'No category data yet.';
            return 'Category breakdown:\n' + cats.map(([cat, amt]) => `  ${cat}: ₹${amt.toLocaleString()}`).join('\n');
        }

        // Transaction count
        if (/how many|count|number of/i.test(q) && /transaction/i.test(q)) {
            return `${stats.transactionCount} transactions this month.`;
        }

        // Fallback
        return `I can help with:\n\n  "How much on food?" — category spend\n  "Am I on track?" — target progress\n  "Biggest expense?" — top transaction\n  "Daily average?" — daily spend avg\n  "How much saved?" — savings summary\n  "Compare to last month" — month comparison\n  "Category breakdown" — all categories`;
    };

    const handleSend = (text = null) => {
        const message = text || input;
        if (!message.trim()) return;

        setMessages(prev => [...prev, { role: 'user', text: message }]);
        setInput('');

        setTimeout(() => {
            const response = processQuery(message);
            setMessages(prev => [...prev, { role: 'assistant', text: response }]);
        }, 300);
    };

    return (
        <div className="fixed bottom-6 right-6 z-[60]">
            {!isOpen ? (
                <button onClick={() => setIsOpen(true)}
                    className="w-14 h-14 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center text-white shadow-glow hover:scale-105 hover:shadow-glow-lg transition-all duration-300">
                    <FaRobot className="text-xl" />
                </button>
            ) : (
                <div className="w-96 max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-4rem)] bg-dark-500 rounded-2xl shadow-card-hover border border-white/[0.06] flex flex-col overflow-hidden animate-spring">
                    <div className="p-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <FaRobot />
                            <div>
                                <span className="font-semibold text-sm tracking-tight">Strategic Intelligence</span>
                                <div className="text-[10px] opacity-70">Finance Assistant</div>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-lg p-1 transition-colors"><FaTimes /></button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] p-3 rounded-2xl text-xs font-medium leading-relaxed whitespace-pre-line ${
                                    msg.role === 'user'
                                        ? 'bg-accent-500/20 text-accent-200 border border-accent-500/20'
                                        : 'bg-white/[0.03] text-frost-800 border border-white/[0.06]'
                                }`}>
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Suggested Questions */}
                    <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                        {SUGGESTED_QUESTIONS.map(q => (
                            <button key={q} onClick={() => handleSend(q)}
                                className="px-2.5 py-1 bg-white/[0.03] border border-white/[0.06] rounded-full text-[10px] text-frost-400 hover:text-accent-300 hover:border-accent-500/30 transition-all">
                                {q}
                            </button>
                        ))}
                    </div>

                    <div className="p-4 border-t border-white/[0.06] flex items-center gap-2">
                        <input type="text" value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Ask about spending, savings, budgets..."
                            className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2 text-xs text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500/30 transition-all placeholder:text-frost-400"
                        />
                        <button onClick={() => handleSend()}
                            className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-600 text-white rounded-lg flex items-center justify-center text-xs hover:shadow-glow transition-all">
                            <FaPaperPlane />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIChatbox;
